import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TeacherIdCard, { CR80_W, CR80_H, GuillocheSVG } from './TeacherIdCard';

const PAGE_W         = 794;
const PAGE_H         = 1123;
const PAD            = 20;
const COLS           = 2;
const ROWS           = 3;
const GAP            = 12;
const CARD_W         = Math.floor((PAGE_W - PAD * 2 - GAP * (COLS - 1)) / COLS);
const CARD_H         = Math.round(CARD_W * (CR80_H / CR80_W));
const CARDS_PER_PAGE = COLS * ROWS;
const CR80_SCALE     = CARD_W / CR80_W;

const PRINT_STYLE = `
  @media print {
    @page { size: 210mm 297mm portrait; margin: 0; }
    body > * { display: none !important; }
    #teacher-id-card-print-root { display: block !important; position: static !important; background: transparent !important; overflow: visible !important; padding: 0 !important; }
    .print-page { page-break-after: always; box-shadow: none !important; transform: none !important; width: 210mm !important; height: 297mm !important; }
    .print-page:last-child { page-break-after: avoid; }
  }
`;

const BLOOD_GROUP_OPTIONS = [
  { label: 'Not Available (N/A)', value: 'N/A' },
  { label: 'A+', value: 'A+' },
  { label: 'A-', value: 'A-' },
  { label: 'B+', value: 'B+' },
  { label: 'B-', value: 'B-' },
  { label: 'O+', value: 'O+' },
  { label: 'O-', value: 'O-' },
  { label: 'AB+', value: 'AB+' },
  { label: 'AB-', value: 'AB-' },
];

// ── QR pattern for back side ──────────────────────────────────────────────────
const BackQR = ({ size = 52 }) => {
  const cells = [
    [1,1,1,1,1,1,1,0,1,0,1,1,1,1,1,1,1],
    [1,0,0,0,0,0,1,0,0,1,1,0,0,0,0,0,1],
    [1,0,1,1,1,0,1,0,1,0,1,0,1,1,1,0,1],
    [1,0,1,1,1,0,1,0,0,1,1,0,1,1,1,0,1],
    [1,0,0,0,0,0,1,0,1,0,1,0,0,0,0,0,1],
    [1,1,1,1,1,1,1,0,1,0,1,1,1,1,1,1,1],
    [0,0,0,0,0,0,0,0,1,1,0,0,0,0,0,0,0],
    [1,0,1,1,0,1,0,1,0,1,1,0,1,0,1,1,0],
    [0,1,1,0,1,0,1,0,1,0,0,1,0,1,1,0,1],
    [1,0,0,1,0,1,0,1,0,1,1,0,1,0,0,1,0],
    [0,0,0,0,0,0,0,0,1,0,0,1,0,1,0,0,1],
    [1,1,1,1,1,1,1,0,0,1,1,0,1,0,1,1,0],
    [1,0,0,0,0,0,1,0,1,0,0,1,0,1,0,0,1],
    [1,0,1,1,1,0,1,0,0,1,1,0,1,0,1,1,0],
    [1,0,1,1,1,0,1,0,1,0,0,1,0,1,0,0,1],
    [1,0,0,0,0,0,1,0,0,1,1,0,1,0,1,1,0],
    [1,1,1,1,1,1,1,0,1,0,0,1,0,1,0,0,1],
  ];
  const n = cells.length;
  const c = size / n;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg">
      <rect width={size} height={size} fill="white" rx="2"/>
      {cells.map((row, r) => row.map((v, col) =>
        v ? <rect key={`${r}-${col}`} x={col*c} y={r*c} width={c} height={c} fill="#0B2545"/> : null
      ))}
    </svg>
  );
};

// ── BACK CARD ─────────────────────────────────────────────────────────────────
const TeacherCardBack = ({ teacher, academicYear = '2026-2027', overrides = {} }) => {
  const d = { ...teacher, ...overrides };
  const designation = d.role === 'tuition_teacher' ? 'Tuition Teacher'
    : d.designation || (d.class ? `Class ${d.class} Teacher` : 'Senior Teacher');
  const empId = d.empId || d.employeeId || d.studentId
    || `TRS-EMP-2026-${String(d._id||'001').slice(-3).padStart(3,'0')}`;
  const rules = d.rules || [
    'This card is non-transferable and remains the official property of The Rising School.',
    "Loss of this card must be immediately reported to the Principal's Office.",
    'Must be displayed at all times while on school premises and official duties.',
    'Surrender this card upon cessation of employment or relieving.',
  ];
  const HEADER_H = 62;
  const FOOTER_H = 52;
  const BODY_H   = CR80_H - HEADER_H - FOOTER_H;

  return (
    <div style={{
      width: CR80_W, height: CR80_H, borderRadius: 22,
      overflow: 'hidden', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column',
      fontFamily: '"Arial","Helvetica Neue",sans-serif',
      border: '1.5px solid rgba(11,37,69,0.2)',
      boxShadow: '0 6px 28px rgba(11,37,69,0.25)',
      position: 'relative', background: '#eaf4f9',
    }}>
      <GuillocheSVG />

      {/* HEADER */}
      <div style={{
        height: HEADER_H, flexShrink: 0, background: '#0B2545',
        borderBottom: '2.5px solid #c9a84c',
        display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12,
        position: 'relative', zIndex: 2,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%', background: '#fff',
          border: '2px solid rgba(201,168,76,0.9)',
          boxShadow: '0 0 0 1.5px rgba(11,37,69,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', flexShrink: 0,
        }}>
          <img src="/logo.png" alt=""
            style={{ width: '82%', height: '82%', objectFit: 'contain' }}
            onError={e => { e.target.style.display='none'; e.target.parentElement.innerText='⚜'; }}
          />
        </div>
        <div style={{ lineHeight: 1 }}>
          <div style={{ color:'#fff', fontSize:15, fontWeight:900, letterSpacing:'0.9px', textTransform:'uppercase', fontFamily:'Georgia,serif' }}>
            THE RISING SCHOOL
          </div>
          <div style={{ color:'#E2B93B', fontSize:9, fontWeight:700, letterSpacing:'1px', marginTop:5, textTransform:'uppercase' }}>
            INSTRUCTIONS &amp; CAMPUS CODE
          </div>
        </div>
      </div>

      {/* BODY — balanced 2-column grid */}
      <div style={{
        height: BODY_H, flexShrink: 0,
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        position: 'relative', zIndex: 2,
      }}>
        {/* Left column */}
        <div style={{
          padding: '12px 12px 10px 16px',
          display: 'flex', flexDirection: 'column', gap: 10,
          borderRight: '1px solid rgba(11,37,69,0.14)',
        }}>
          {/* Faculty box */}
          <div style={{
            background: 'rgba(11,37,69,0.07)', border: '1px solid rgba(11,37,69,0.2)',
            borderRadius: 6, padding: '8px 10px',
          }}>
            <div style={{ fontSize:7, color:'#555', textTransform:'uppercase', letterSpacing:'0.7px', fontWeight:700, marginBottom:4 }}>
              FACULTY VERIFICATION
            </div>
            <div style={{ fontSize:13, fontWeight:900, color:'#000', textTransform:'uppercase', letterSpacing:'0.3px', lineHeight:1.2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              {(d.name||'N/A').toUpperCase()}
            </div>
            <div style={{ fontSize:10, fontWeight:600, color:'#0B2545', marginTop:3, lineHeight:1.2 }}>
              {designation.toUpperCase()}
            </div>
            <div style={{ fontSize:9, fontWeight:700, color:'#222', fontFamily:'"Courier New",monospace', marginTop:3, letterSpacing:'0.5px' }}>
              {empId}
            </div>
          </div>

          {/* Terms */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize:7.5, fontWeight:800, color:'#0B2545', textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:6, borderBottom:'1.5px solid rgba(11,37,69,0.18)', paddingBottom:4 }}>
              TERMS &amp; CONDITIONS
            </div>
            {rules.map((rule, i) => (
              <div key={i} style={{ display:'flex', gap:6, marginBottom:6, fontSize:8, color:'#111', lineHeight:1.35 }}>
                <span style={{ fontWeight:800, color:'#0B2545', flexShrink:0, minWidth:12 }}>{i+1}.</span>
                <span>{rule}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div style={{ padding:'12px 16px 10px 12px', display:'flex', flexDirection:'column', gap:10 }}>
          <div style={{ background:'rgba(11,37,69,0.07)', border:'1px solid rgba(11,37,69,0.2)', borderRadius:6, padding:'8px 10px' }}>
            <div style={{ fontSize:7, color:'#555', textTransform:'uppercase', letterSpacing:'0.7px', fontWeight:700, marginBottom:5 }}>
              IF FOUND, PLEASE RETURN TO:
            </div>
            <div style={{ fontSize:9, fontWeight:800, color:'#0B2545', lineHeight:1.3 }}>The Rising School</div>
            <div style={{ fontSize:8, fontWeight:600, color:'#333', lineHeight:1.4, marginTop:2 }}>Managed by The Synergy Foundation</div>
            <div style={{ fontSize:8, fontWeight:600, color:'#333', lineHeight:1.4 }}>Sector 12, Savina, Udaipur</div>
            <div style={{ fontSize:8, fontWeight:600, color:'#333', lineHeight:1.4 }}>Rajasthan — 313002</div>
            <div style={{ marginTop:6, display:'flex', flexDirection:'column', gap:3 }}>
              <div style={{ fontSize:8, fontWeight:700, color:'#0B2545' }}>Ph: +91 8209683549</div>
              <div style={{ fontSize:8, fontWeight:600, color:'#333' }}>contact@therisingschool.com</div>
              <div style={{ fontSize:8, fontWeight:600, color:'#333' }}>www.therisingschool.com</div>
            </div>
          </div>
          <div style={{ background:'#0B2545', borderRadius:5, padding:'6px 10px', textAlign:'center' }}>
            <div style={{ fontSize:7, color:'#c9a84c', fontWeight:700, letterSpacing:'0.6px', textTransform:'uppercase' }}>Academic Year</div>
            <div style={{ fontSize:11, color:'#fff', fontWeight:800, letterSpacing:'0.5px', marginTop:2 }}>{academicYear}</div>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{
        height: FOOTER_H, flexShrink:0, background:'#0B2545',
        borderTop:'2.5px solid #c9a84c',
        display:'flex', alignItems:'center', padding:'0 16px', gap:14,
        position:'relative', zIndex:2,
      }}>
        <div style={{ flexShrink:0 }}><BackQR size={38} /></div>
        <div style={{ fontSize:7.5, color:'rgba(255,255,255,0.7)', lineHeight:1.5 }}>
          <div style={{ fontWeight:600 }}>Scan to verify staff credentials</div>
          <div style={{ color:'rgba(255,255,255,0.45)', fontSize:7 }}>therisingschool.com/verify</div>
        </div>
        <div style={{ flex:1 }} />
        <div style={{ textAlign:'right', flexShrink:0 }}>
          <div style={{ height:18 }} />
          <div style={{ width:110, borderTop:'1.5px solid rgba(201,168,76,0.8)', paddingTop:5, marginLeft:'auto' }}>
            <div style={{ fontSize:8, color:'rgba(255,255,255,0.88)', letterSpacing:'0.5px', textTransform:'uppercase', fontWeight:700 }}>Authorized Signatory</div>
            <div style={{ fontSize:7, color:'rgba(255,255,255,0.5)', marginTop:2 }}>The Rising School</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Scaled card wrapper ───────────────────────────────────────────────────────
const ScaledCard = ({ teacher, side, academicYear, overrides }) => (
  <div style={{ width: CARD_W, height: CARD_H, overflow: 'hidden', borderRadius: 12 }}>
    <div style={{ transform: `scale(${CR80_SCALE})`, transformOrigin: 'top left', width: CR80_W, height: CR80_H }}>
      {side === 'front'
        ? <TeacherIdCard teacher={teacher} academicYear={academicYear} overrides={overrides} />
        : <TeacherCardBack teacher={teacher} academicYear={academicYear} overrides={overrides} />
      }
    </div>
  </div>
);

// ── Card grid ─────────────────────────────────────────────────────────────────
const CardGrid = ({ teachers, side, academicYear, allOverrides }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(${COLS}, ${CARD_W}px)`,
    gridTemplateRows: `repeat(${ROWS}, ${CARD_H}px)`,
    gap: GAP, flexShrink: 0,
  }}>
    {Array.from({ length: CARDS_PER_PAGE }).map((_, i) => {
      const t = teachers[i];
      return t
        ? <ScaledCard key={t._id} teacher={t} side={side} academicYear={academicYear} overrides={allOverrides[t._id] || {}} />
        : <div key={`e-${i}`} style={{ width: CARD_W, height: CARD_H, background: '#f3f4f6', borderRadius: 12, border: '1px dashed #d1d5db' }} />;
    })}
  </div>
);

// ── Print page ────────────────────────────────────────────────────────────────
const TeacherIdCardPage = React.forwardRef(({ pageTeachers, pageNum, totalPages, side, academicYear, allOverrides }, ref) => (
  <div ref={ref} className="print-page" style={{
    width: PAGE_W, height: PAGE_H, background: '#fff', padding: PAD,
    boxSizing: 'border-box', fontFamily: 'Arial,sans-serif', display: 'flex', flexDirection: 'column',
  }}>
    <div style={{ textAlign:'center', fontSize:9, color:'#6b7280', borderBottom:'0.8px solid #e5e7eb', paddingBottom:4, marginBottom:8, flexShrink:0 }}>
      The Rising School — Staff ID Cards — Page {pageNum} of {totalPages}
    </div>
    <div style={{ fontSize:9, fontWeight:'bold', color: side==='front'?'#1e40af':'#7c3aed', marginBottom:6, flexShrink:0 }}>
      {side === 'front' ? '▶ FRONT SIDE — Print on Side 1' : '▶ BACK SIDE — Print on Side 2'}
    </div>
    <CardGrid teachers={pageTeachers} side={side} academicYear={academicYear} allOverrides={allOverrides} />
    <div style={{ marginTop:'auto', paddingTop:6, textAlign:'center', fontSize:7, color:'#9ca3af', flexShrink:0 }}>
      Printed by The Rising School Management System — Academic Year {academicYear}
    </div>
  </div>
));

// ── Field input helper ────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: '#374151', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
    {children}
  </div>
);

const inp = {
  width: '100%', padding: '6px 9px', borderRadius: 6,
  border: '1.5px solid #cbd5e1', fontSize: 12, fontFamily: 'Arial,sans-serif',
  boxSizing: 'border-box', outline: 'none', background: '#fff',
};

// ── Per-teacher inline editor ─────────────────────────────────────────────────
const TeacherEditor = ({ teacher, overrides, onChange, onReset, onSave, saving }) => {
  const d = { ...teacher, ...overrides };
  const fileRef = useRef();

  const set = (key, val) => onChange({ ...overrides, [key]: val });

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    onChange({ ...overrides, photoBlob: url });
  };

  const photoUrl = overrides.photoBlob
    || (d.profilePicture
      ? (d.profilePicture.startsWith('http') ? d.profilePicture : `${API_BASE_URL}${d.profilePicture}`)
      : null);

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
      {/* Photo upload */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.4px' }}>PHOTO</div>
        <div
          onClick={() => fileRef.current.click()}
          style={{
            width: 80, height: 100,
            border: '1.5px solid #0B2545',
            borderRadius: 4, overflow: 'hidden',
            background: '#e4ecf3',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', position: 'relative',
          }}
        >
          {photoUrl
            ? <img src={photoUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center top', imageRendering:'-webkit-optimize-contrast' }} />
            : <div style={{ textAlign:'center', color:'#9ca3af', fontSize:10, fontWeight:600 }}>
                <div style={{ fontSize:22, marginBottom:4 }}>📷</div>
                <div>UPLOAD</div>
              </div>
          }
          <div style={{
            position:'absolute', inset:0, background:'rgba(11,37,69,0.45)',
            display:'flex', alignItems:'center', justifyContent:'center',
            opacity:0, transition:'opacity 0.2s',
            fontSize:10, color:'#fff', fontWeight:700,
          }}
            onMouseEnter={e => e.currentTarget.style.opacity=1}
            onMouseLeave={e => e.currentTarget.style.opacity=0}
          >CHANGE</div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handlePhoto} />
        <div style={{ fontSize:9, color:'#6b7280', marginTop:4, textAlign:'center' }}>Click to change</div>
      </div>

      {/* Fields */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Full Name">
              <input style={inp} value={d.name||''} onChange={e => set('name', e.target.value)} placeholder="Teacher Full Name" />
            </Field>
          </div>
          <Field label="Designation">
            <input style={inp} value={d.designation||''} onChange={e => set('designation', e.target.value)} placeholder="e.g. Senior Educator" />
          </Field>
          <Field label="Subject">
            <input style={inp} value={d.subject||''} onChange={e => set('subject', e.target.value)} placeholder="e.g. Mathematics" />
          </Field>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Employee ID">
              <input style={{ ...inp, fontFamily:'"Courier New",monospace' }}
                value={d.empId || d.employeeId || ''}
                onChange={e => set('empId', e.target.value)}
                placeholder="TRS-EMP-2026-001"
              />
            </Field>
          </div>
          <Field label="Date of Issue">
            <input style={inp} value={d.issueDate||'15 JUN 2026'} onChange={e => set('issueDate', e.target.value)} placeholder="15 JUN 2026" />
          </Field>
          <Field label="Valid Thru">
            <input style={inp} value={d.validThru||''} onChange={e => set('validThru', e.target.value)} placeholder="31 MAY 2029" />
          </Field>
          <Field label="Blood Group">
            <select style={inp} value={d.bloodGroup||'N/A'} onChange={e => set('bloodGroup', e.target.value)}>
              {BLOOD_GROUP_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </Field>
          <Field label="Contact Number">
            <input style={inp} value={d.contact || d.mobile || ''} onChange={e => set('contact', e.target.value)} placeholder="+91 8209683549" />
          </Field>
        </div>

        <Field label="Campus Rules (Back Side — one per line)">
          <textarea
            style={{ ...inp, height: 72, resize: 'vertical', lineHeight: 1.5 }}
            value={(d.rules || [
              'This card is non-transferable and remains the official property of The Rising School.',
              "Loss of this card must be immediately reported to the Principal's Office.",
              'Must be displayed at all times while on school premises and official duties.',
              'Surrender this card upon cessation of employment or relieving.',
            ]).join('\n')}
            onChange={e => set('rules', e.target.value.split('\n').filter(Boolean))}
          />
        </Field>

        <div style={{ display:'flex', gap:8, marginTop:2 }}>
          <button onClick={onReset} disabled={saving} style={{
            padding: '6px 14px', background: '#f1f5f9', border: '1.5px solid #cbd5e1',
            borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: saving?'not-allowed':'pointer', color: '#374151',
          }}>↺ Reset to Original</button>
          <button onClick={() => onSave(teacher._id, overrides)} disabled={saving} style={{
            padding: '6px 18px',
            background: saving ? '#9ca3af' : 'linear-gradient(135deg,#0B2545,#1e40af)',
            color: '#fff', border: 'none', borderRadius: 6,
            fontSize: 11, fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
            boxShadow: saving ? 'none' : '0 2px 8px rgba(11,37,69,0.35)',
          }}>{saving ? '⏳ Saving...' : '💾 Save Changes'}</button>
        </div>
      </div>
    </div>
  );
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
function BulkTeacherIdCardPrint({ user, onClose, academicYear = '2026-2027' }) {
  const [mode, setMode]             = useState('all');
  const [allTeachers, setAll]       = useState([]);
  const [search, setSearch]         = useState('');
  const [selectedIds, setSelected]  = useState(new Set());
  const [loading, setLoading]       = useState(false);
  const [downloading, setDL]        = useState(false);
  const [error, setError]           = useState('');
  const [progress, setProgress]     = useState('');
  const [donePages, setDone]        = useState(0);
  const [allOverrides, setOverrides]= useState({});
  const [editingId, setEditingId]   = useState(null);
  const [savingId, setSavingId]     = useState(null);
  const [toast, setToast]           = useState('');
  const pageRefs = useRef([]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleSaveTeacher = async (teacherId, overrides) => {
    setSavingId(teacherId);
    try {
      const payload = {
        name:        overrides.name,
        designation: overrides.designation,
        subject:     overrides.subject,
        employeeId:  overrides.empId || overrides.employeeId,
        issueDate:   overrides.issueDate,
        validThru:   overrides.validThru,
        bloodGroup:  overrides.bloodGroup,
        mobile:      overrides.contact || overrides.mobile,
        rules:       overrides.rules,
      };
      // Strip undefined keys so we don't overwrite with blanks
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      const res = await axios.put(`${API_BASE_URL}/api/users/${teacherId}`, payload);
      const updated = res.data?.user || res.data;

      // Merge saved data back into allTeachers so previews & PDF reflect it
      setAll(prev => prev.map(t => t._id === teacherId ? { ...t, ...updated } : t));

      // Clear overrides for this teacher — DB is now the source of truth
      setOverrides(prev => { const n = { ...prev }; delete n[teacherId]; return n; });

      showToast('✅ Changes saved successfully!');
    } catch (err) {
      showToast('❌ Save failed: ' + (err.response?.data?.error || err.response?.data?.message || err.message));
    } finally {
      setSavingId(null);
    }
  };

  useEffect(() => { fetchTeachers(); }, []);

  const fetchTeachers = async () => {
    try {
      setLoading(true); setError('');
      const res = await axios.get(`${API_BASE_URL}/api/users`);
      setAll(res.data.filter(u => u.role === 'teacher' || u.role === 'tuition_teacher'));
    } catch { setError('Failed to fetch teachers'); }
    finally { setLoading(false); }
  };

  const toggleTeacher = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const filtered = allTeachers.filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.email?.toLowerCase().includes(search.toLowerCase()) ||
    (t.class||'').toLowerCase().includes(search.toLowerCase())
  );

  const toggleAll = () => {
    const ids = filtered.map(t => t._id);
    const allSel = ids.every(id => selectedIds.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => allSel ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const active = mode === 'all'
    ? allTeachers
    : [...selectedIds].map(id => allTeachers.find(t => t._id === id)).filter(Boolean);

  const batches = [];
  for (let i = 0; i < active.length; i += CARDS_PER_PAGE)
    batches.push(active.slice(i, i + CARDS_PER_PAGE));

  const renderPages = batches.flatMap((batch, bi) => [
    { batch, side: 'front', batchIdx: bi },
    { batch, side: 'back',  batchIdx: bi },
  ]);
  const totalPages = renderPages.length;
  const pct = totalPages ? Math.round((donePages / totalPages) * 100) : 0;
  const canDownload = mode === 'all' ? allTeachers.length > 0 : selectedIds.size > 0;
  const pdfName = mode === 'all' ? 'All_Teacher_ID_Cards.pdf' : `Selected_Teacher_ID_Cards_${active.length}.pdf`;

  const handleDownloadPDF = async () => {
    if (!renderPages.length) return;
    try {
      setDL(true); setDone(0); setProgress('Preparing…');
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF }   = await import('jspdf');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      for (let i = 0; i < pageRefs.current.length; i++) {
        setProgress(`Rendering page ${i+1} of ${pageRefs.current.length}…`);
        const el = pageRefs.current[i];
        if (!el) continue;
        const canvas = await html2canvas(el, {
          scale: 2, useCORS: true, allowTaint: true,
          backgroundColor: '#ffffff', logging: false,
          width: PAGE_W, height: PAGE_H,
        });
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 210, 297);
        setDone(i + 1);
      }
      setProgress('Saving…');
      pdf.save(pdfName);
      setProgress(''); setDone(0);
    } catch (err) {
      setError('PDF failed: ' + err.message); setProgress('');
    } finally { setDL(false); }
  };

  const btnBase = { border: 'none', borderRadius: 8, padding: '9px 0', fontWeight: 700, fontSize: 13, cursor: 'pointer', flex: 1, transition: 'all 0.2s' };

  return (
    <div id="teacher-id-card-print-root" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
      zIndex: 9999, display: 'flex', flexDirection: 'column',
      alignItems: 'center', overflowY: 'auto', padding: '20px 16px',
    }}>
      <style>{PRINT_STYLE}</style>

      {/* ── CONTROL PANEL ── */}
      <div style={{
        background: '#fff', borderRadius: 16, padding: 26,
        width: '100%', maxWidth: 860, marginBottom: 24, flexShrink: 0,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}>
        {/* Header row */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
          <div>
            <h2 style={{ margin:0, color:'#0B2545', fontSize:20, fontWeight:800 }}>👨🏫 Teacher ID Cards</h2>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>CR80 · Live Edit · Double-Sided Print</div>
          </div>
          <button onClick={onClose} style={{ background:'#dc2626', color:'#fff', border:'none', borderRadius:8, padding:'7px 14px', cursor:'pointer', fontWeight:'bold' }}>✕ Close</button>
        </div>

        {/* Mode toggle */}
        <div style={{ display:'flex', gap:6, marginBottom:20, background:'#f1f5f9', borderRadius:10, padding:4 }}>
          {[['all','📋 All Teachers'],['selected','☑️ Selected Teachers']].map(([val, label]) => (
            <button key={val} onClick={() => { setMode(val); setError(''); pageRefs.current=[]; }}
              style={{ ...btnBase, background: mode===val ? 'linear-gradient(135deg,#0B2545,#1e40af)' : 'transparent', color: mode===val ? '#fff' : '#64748b' }}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'all' && !loading && allTeachers.length > 0 && (
          <div style={{ background:'#eff6ff', border:'1.5px solid #93c5fd', borderRadius:10, padding:'10px 14px', marginBottom:14, fontSize:13, color:'#1e40af' }}>
            👨🏫 {allTeachers.length} staff members — all included. Click any teacher below to edit their card.
          </div>
        )}

        {mode === 'selected' && (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background: selectedIds.size>0?'#eff6ff':'#f8fafc', border:`2px solid ${selectedIds.size>0?'#1e40af':'#e2e8f0'}`, borderRadius:10, padding:'10px 14px', marginBottom:12 }}>
              <div style={{ fontWeight:'bold', color:'#0B2545', fontSize:14 }}>
                ☑️ Selected: <span style={{ fontSize:18, color:'#1e40af' }}>{selectedIds.size}</span>
              </div>
              {selectedIds.size > 0 && (
                <button onClick={() => setSelected(new Set())} style={{ background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:6, padding:'5px 12px', cursor:'pointer', fontWeight:'bold', fontSize:12 }}>✕ Clear</button>
              )}
            </div>
            <input type="text" placeholder="🔍 Search by name, email or class…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width:'100%', padding:'9px 12px', borderRadius:8, border:'1.5px solid #93c5fd', fontSize:13, marginBottom:10, boxSizing:'border-box' }}
            />
          </>
        )}

        {/* Teacher list with inline edit */}
        {!loading && (mode === 'all' ? allTeachers : filtered).length > 0 && (
          <div style={{ border:'1.5px solid #e2e8f0', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
            {mode === 'selected' && (
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 14px', background:'#eff6ff', borderBottom:'1px solid #e2e8f0', cursor:'pointer' }} onClick={toggleAll}>
                <input type="checkbox" readOnly checked={filtered.length>0 && filtered.every(t => selectedIds.has(t._id))} style={{ width:16, height:16 }} />
                <span style={{ fontWeight:'bold', fontSize:13, color:'#0B2545' }}>Select All ({filtered.length} shown)</span>
              </div>
            )}
            <div style={{ maxHeight: 320, overflowY:'auto' }}>
              {(mode === 'all' ? allTeachers : filtered).map(t => {
                const checked = selectedIds.has(t._id);
                const hasEdits = !!allOverrides[t._id] && Object.keys(allOverrides[t._id]).length > 0;
                const isOpen = editingId === t._id;
                return (
                  <div key={t._id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                    {/* Row */}
                    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 14px', background: isOpen?'#f0f9ff': checked?'#eff6ff':'#fff' }}>
                      {mode === 'selected' && (
                        <input type="checkbox" readOnly checked={checked} onClick={() => toggleTeacher(t._id)} style={{ width:16, height:16, cursor:'pointer', flexShrink:0 }} />
                      )}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight: checked||mode==='all'?'bold':'normal', fontSize:13, color:'#1e293b' }}>
                          {t.name}
                          {hasEdits && <span style={{ marginLeft:6, fontSize:10, background:'#fef3c7', color:'#92400e', padding:'1px 6px', borderRadius:10, fontWeight:700 }}>EDITED</span>}
                        </div>
                        <div style={{ fontSize:11, color:'#64748b' }}>
                          {t.role==='tuition_teacher'?'Tuition Teacher':'Teacher'}
                          {t.class ? ` · Class ${t.class}` : ''} · {t.email}
                        </div>
                      </div>
                      <button
                        onClick={() => setEditingId(isOpen ? null : t._id)}
                        style={{ padding:'5px 12px', background: isOpen?'#0B2545':'#eff6ff', color: isOpen?'#fff':'#0B2545', border:`1.5px solid ${isOpen?'#0B2545':'#93c5fd'}`, borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer', flexShrink:0 }}
                      >
                        {isOpen ? '▲ Close' : '✏️ Edit Card'}
                      </button>
                    </div>
                    {/* Inline editor */}
                    {isOpen && (
                      <div style={{ padding:'14px 16px', background:'#f8fafc', borderTop:'1px solid #e2e8f0' }}>
                        <TeacherEditor
                          teacher={t}
                          overrides={allOverrides[t._id] || {}}
                          onChange={ov => setOverrides(prev => ({ ...prev, [t._id]: ov }))}
                          onReset={() => setOverrides(prev => { const n={...prev}; delete n[t._id]; return n; })}
                          onSave={handleSaveTeacher}
                          saving={savingId === t._id}
                        />
                        {/* Mini live preview */}
                        <div style={{ marginTop:14 }}>
                          <div style={{ fontSize:11, fontWeight:700, color:'#374151', marginBottom:8 }}>LIVE PREVIEW</div>
                          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                            {['front','back'].map(side => {
                              const scale = 260 / CR80_W;
                              return (
                                <div key={side}>
                                  <div style={{ fontSize:9, color:'#6b7280', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.5px' }}>{side} side</div>
                                  <div style={{ width:260, height: Math.round(CR80_H*scale), overflow:'hidden', borderRadius:8, boxShadow:'0 2px 12px rgba(0,0,0,0.18)' }}>
                                    <div style={{ transform:`scale(${scale})`, transformOrigin:'top left', width:CR80_W, height:CR80_H }}>
                                      {side==='front'
                                        ? <TeacherIdCard teacher={t} academicYear={academicYear} overrides={allOverrides[t._id]||{}} />
                                        : <TeacherCardBack teacher={t} academicYear={academicYear} overrides={allOverrides[t._id]||{}} />
                                      }
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {toast && (
          <div style={{
            position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)',
            background: toast.startsWith('✅') ? '#0B2545' : '#dc2626',
            color:'#fff', padding:'10px 22px', borderRadius:10,
            fontSize:13, fontWeight:700, zIndex:99999,
            boxShadow:'0 4px 20px rgba(0,0,0,0.35)',
            whiteSpace:'nowrap',
          }}>{toast}</div>
        )}
        {error && <div style={{ background:'#fee2e2', color:'#dc2626', padding:10, borderRadius:8, marginBottom:12, fontSize:13 }}>⚠️ {error}</div>}
        {loading && <div style={{ textAlign:'center', padding:18, color:'#1e40af', fontWeight:'bold' }}>⏳ Loading teacher data…</div>}

        {canDownload && !loading && (
          <>
            <div style={{ background:'#eff6ff', border:'2px solid #1e40af', borderRadius:10, padding:14, marginBottom:16 }}>
              <div style={{ fontWeight:'bold', color:'#0B2545', marginBottom:6, fontSize:14 }}>📊 Print Summary</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'6px 20px', fontSize:13, color:'#374151' }}>
                <div>👨🏫 Teachers: <b>{active.length}</b></div>
                <div>📄 Pages: <b>{totalPages}</b></div>
                <div>✏️ Edited: <b>{Object.keys(allOverrides).length}</b></div>
              </div>
              <div style={{ marginTop:8, fontSize:11, color:'#6b7280' }}>💡 Front + Back per batch. Print duplex for double-sided cards.</div>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={handleDownloadPDF} disabled={downloading} style={{
                flex:1, padding:13,
                background: downloading?'#9ca3af':'linear-gradient(135deg,#0B2545,#1e40af,#3b82f6)',
                color:'#fff', border:'none', borderRadius:10, fontSize:15, fontWeight:'bold',
                cursor: downloading?'not-allowed':'pointer',
                boxShadow:'0 4px 15px rgba(11,37,69,0.4)',
              }}>
                {downloading ? `⏳ ${progress}` : `⬇️ Download PDF — ${totalPages} Pages`}
              </button>
              <button onClick={() => window.print()} style={{
                padding:'13px 20px', background:'#0B2545', color:'#fff',
                border:'none', borderRadius:10, fontSize:14, fontWeight:'bold', cursor:'pointer',
              }}>🖨️ Print</button>
            </div>

            {downloading && (
              <div style={{ marginTop:10 }}>
                <div style={{ background:'#e5e7eb', borderRadius:6, height:10, overflow:'hidden', marginBottom:4 }}>
                  <div style={{ height:'100%', background:'linear-gradient(90deg,#0B2545,#3b82f6)', width:`${pct}%`, transition:'width 0.3s ease', borderRadius:6 }} />
                </div>
                <div style={{ fontSize:11, color:'#6b7280', textAlign:'center' }}>{donePages} / {totalPages} pages ({pct}%)</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── PRINT PREVIEW ── */}
      {canDownload && !loading && (
        <div style={{ width:'100%', maxWidth:860 }}>
          <div style={{ color:'#fff', fontWeight:'bold', fontSize:15, marginBottom:16, textAlign:'center' }}>
            👁️ Print Preview — {totalPages} Pages
            <span style={{ fontSize:11, fontWeight:'normal', color:'#9ca3af', marginLeft:8 }}>(scaled to fit)</span>
          </div>
          {renderPages.map(({ batch, side, batchIdx }, idx) => {
            const previewW = Math.min(820, PAGE_W);
            const scale    = previewW / PAGE_W;
            const scaledH  = Math.round(PAGE_H * scale);
            return (
              <div key={idx} style={{ marginBottom:32 }}>
                <div style={{ color: side==='front'?'#93c5fd':'#a78bfa', fontWeight:'bold', fontSize:12, marginBottom:6, textAlign:'center' }}>
                  ── Page {idx+1} of {totalPages} · Batch {batchIdx+1} — {side==='front'?'▶ FRONT':'▶ BACK'} ──
                </div>
                <div style={{ width:previewW, height:scaledH, overflow:'hidden', borderRadius:10, boxShadow:'0 6px 24px rgba(0,0,0,0.5)', margin:'0 auto' }}>
                  <div style={{ transform:`scale(${scale})`, transformOrigin:'top left', width:PAGE_W, height:PAGE_H }}>
                    <TeacherIdCardPage
                      ref={el => { pageRefs.current[idx] = el; }}
                      pageTeachers={batch}
                      pageNum={idx+1}
                      totalPages={totalPages}
                      side={side}
                      academicYear={academicYear}
                      allOverrides={allOverrides}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BulkTeacherIdCardPrint;
